import { useEffect, useRef, useState } from 'react';
import Modal from '../../../shared/components/Modal';
import { GRADE_LEVELS } from '../../../shared/constant/gradeLevels';
import { CURRENT_SCHOOL_YEAR, getSchoolYearOptions } from '../../../shared/constant/schoolYears';
import { deriveTrackFromStrand, getStrandsForGradeLevel } from '../../../shared/constant/strands';

const FIELD_CLASS =
  'w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#4ea6d0]';

// Shows what was read from an SF1 file before anything is saved. The section details apply to every
// student in the file and can be corrected here. `existingLrns` are skipped. `allowedGroups`: for
// teachers, the [{ grade_level, section }] pairs they may add students to (see StudentFormModal).
function ImportReviewModal({ fileName, details, students, existingLrns, allowedGroups, onClose, onImport }) {
  // The section starts as the one on the SF1. For a teacher it's matched to their own sections
  // ignoring case and spacing (so "RIZAL" finds "Rizal"); only a section that isn't theirs falls back
  // to their first one.
  const normalize = (value) => String(value ?? '').trim().replace(/\s+/g, ' ').toLowerCase();
  const fileGroup = allowedGroups?.find(
    (g) => g.grade_level === details.grade_level && normalize(g.section) === normalize(details.section)
  );
  const fileGroupAllowed = !allowedGroups || Boolean(fileGroup);
  const [gradeLevel, setGradeLevel] = useState(
    fileGroupAllowed ? details.grade_level : allowedGroups[0]?.grade_level ?? ''
  );
  const [section, setSection] = useState(
    !allowedGroups ? details.section : (fileGroup ?? allowedGroups[0])?.section ?? ''
  );
  const [schoolYear, setSchoolYear] = useState(details.school_year || CURRENT_SCHOOL_YEAR);
  const [strand, setStrand] = useState(details.strand);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState('');

  const gradeLevelOptions = allowedGroups
    ? GRADE_LEVELS.filter((g) => allowedGroups.some((group) => group.grade_level === g.value))
    : GRADE_LEVELS;
  const sectionOptions = allowedGroups
    ? [...new Set(allowedGroups.filter((g) => g.grade_level === gradeLevel).map((g) => g.section))].sort()
    : null;
  const strandOptions = getStrandsForGradeLevel(gradeLevel);
  const schoolYearOptions = getSchoolYearOptions();
  if (schoolYear && !schoolYearOptions.includes(schoolYear)) schoolYearOptions.unshift(schoolYear);

  const statusOf = (student) => {
    if (student.error) return { label: student.error, tone: 'text-red-600' };
    if (existingLrns.has(student.lrn)) return { label: 'Already exists — skipped', tone: 'text-gray-500' };
    return { label: 'Ready', tone: 'text-green-600' };
  };
  const ready = students.filter((s) => !s.error && !existingLrns.has(s.lrn));
  const isImportable = (student) => !student.error && !existingLrns.has(student.lrn);

  // Importable students' LRNs (unique among them) that are ticked; all of them to start with.
  const [selected, setSelected] = useState(() => new Set(ready.map((s) => s.lrn)));
  const chosen = ready.filter((s) => selected.has(s.lrn));
  const allSelected = ready.length > 0 && chosen.length === ready.length;
  const selectAllRef = useRef(null);
  useEffect(() => {
    if (selectAllRef.current) selectAllRef.current.indeterminate = chosen.length > 0 && !allSelected;
  }, [chosen.length, allSelected]);

  const toggleStudent = (lrn) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(lrn)) next.delete(lrn);
      else next.add(lrn);
      return next;
    });
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(ready.map((s) => s.lrn)));

  const handleGradeLevelChange = (value) => {
    setGradeLevel(value);
    if (allowedGroups) setSection(allowedGroups.find((g) => g.grade_level === value)?.section ?? '');
    if (!getStrandsForGradeLevel(value).some((s) => s.code === strand)) setStrand('');
  };

  const handleImport = async () => {
    setError('');
    if (!gradeLevel) {
      setError('Choose the grade level for these students.');
      return;
    }
    if (allowedGroups && !section) {
      setError('Choose one of your sections for these students.');
      return;
    }

    const showStrand = strandOptions.length > 0;
    setImporting(true);
    try {
      await onImport(
        chosen.map(({ error: _error, ...student }) => ({
          ...student,
          grade_level: gradeLevel,
          section: section.trim() || null,
          school_year: schoolYear || null,
          strand: showStrand ? strand || null : null,
          track: showStrand ? deriveTrackFromStrand(strand) : null,
        }))
      );
    } catch (err) {
      setError(err.message || 'Failed to import students');
      setImporting(false);
    }
  };

  return (
    <Modal title="Import from SF1" onClose={importing ? () => {} : onClose} maxWidthClass="max-w-3xl">
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          <span className="font-medium text-gray-800">{fileName}</span> — {students.length} student
          {students.length === 1 ? '' : 's'} found. Check the section details below; they apply to everyone in the file.
        </p>

        {!fileGroupAllowed && (
          <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
            This file is for Grade {details.grade_level || '?'}
            {details.section ? ` - ${details.section}` : ''}, which isn't one of your sections. Choose one of yours below.
          </p>
        )}

        {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</p>}

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="space-y-1">
            <label htmlFor="importGrade" className="text-sm font-medium text-gray-700">Grade Level</label>
            <select
              id="importGrade"
              value={gradeLevel}
              onChange={(e) => handleGradeLevelChange(e.target.value)}
              className={FIELD_CLASS}
            >
              {!gradeLevel && <option value="">Select…</option>}
              {gradeLevelOptions.map((g) => (
                <option key={g.value} value={g.value}>{g.label}</option>
              ))}
            </select>
          </div>

          <div className="space-y-1">
            <label htmlFor="importSection" className="text-sm font-medium text-gray-700">Section</label>
            {sectionOptions ? (
              <select id="importSection" value={section} onChange={(e) => setSection(e.target.value)} className={FIELD_CLASS}>
                {sectionOptions.map((name) => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
            ) : (
              <input
                id="importSection"
                type="text"
                value={section}
                onChange={(e) => setSection(e.target.value)}
                className={FIELD_CLASS}
              />
            )}
          </div>

          <div className="space-y-1">
            <label htmlFor="importSchoolYear" className="text-sm font-medium text-gray-700">School Year</label>
            <select
              id="importSchoolYear"
              value={schoolYear}
              onChange={(e) => setSchoolYear(e.target.value)}
              className={FIELD_CLASS}
            >
              <option value="">—</option>
              {schoolYearOptions.map((year) => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>

          {strandOptions.length > 0 && (
            <div className="space-y-1">
              <label htmlFor="importStrand" className="text-sm font-medium text-gray-700">Strand</label>
              <select id="importStrand" value={strand} onChange={(e) => setStrand(e.target.value)} className={FIELD_CLASS}>
                <option value="">Select…</option>
                {strandOptions.map((s) => (
                  <option key={s.code} value={s.code}>{s.code}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div className="max-h-80 overflow-y-auto rounded-md border border-gray-200">
          <table className="w-full text-xs">
            <thead className="bg-gray-50 text-left text-gray-500 sticky top-0">
              <tr>
                <th className="w-8 px-3 py-2">
                  <input
                    ref={selectAllRef}
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleAll}
                    disabled={ready.length === 0}
                    aria-label="Select all students"
                    className="h-4 w-4 rounded border-gray-300 accent-[#0b4a71]"
                  />
                </th>
                <th className="px-3 py-2 font-medium">LRN</th>
                <th className="px-3 py-2 font-medium">Name</th>
                <th className="px-3 py-2 font-medium">Sex</th>
                <th className="px-3 py-2 font-medium">Age</th>
                <th className="px-3 py-2 font-medium">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {students.map((student, i) => {
                const status = statusOf(student);
                const importable = isImportable(student);
                return (
                  <tr
                    key={`${student.lrn}-${i}`}
                    onClick={importable ? () => toggleStudent(student.lrn) : undefined}
                    className={student.error ? 'bg-red-50/50' : importable ? 'cursor-pointer hover:bg-gray-50' : undefined}
                  >
                    <td className="px-3 py-1.5">
                      <input
                        type="checkbox"
                        checked={importable && selected.has(student.lrn)}
                        onChange={() => toggleStudent(student.lrn)}
                        onClick={(e) => e.stopPropagation()}
                        disabled={!importable}
                        aria-label={`Import ${student.firstname} ${student.lastname}`}
                        className="h-4 w-4 rounded border-gray-300 accent-[#0b4a71] disabled:opacity-40"
                      />
                    </td>
                    <td className="px-3 py-1.5 text-gray-600 whitespace-nowrap">{student.lrn || '—'}</td>
                    <td className="px-3 py-1.5 text-gray-700">
                      {student.lastname}, {student.firstname} {student.middlename || ''}
                    </td>
                    <td className="px-3 py-1.5 text-gray-600">{student.gender || '—'}</td>
                    <td className="px-3 py-1.5 text-gray-600">{student.age ?? '—'}</td>
                    <td className={`px-3 py-1.5 font-medium ${status.tone}`}>{status.label}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-end gap-2">
          <p className="mr-auto text-sm text-gray-500">
            {chosen.length} of {ready.length} selected
          </p>
          <button
            type="button"
            onClick={onClose}
            disabled={importing}
            className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleImport}
            disabled={importing || chosen.length === 0}
            className="rounded-md bg-[#0b4a71] px-4 py-2 text-sm font-medium text-white hover:bg-[#3f6d8e] disabled:opacity-50"
          >
            {importing ? 'Importing…' : `Import ${chosen.length} Student${chosen.length === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>
    </Modal>
  );
}

export default ImportReviewModal;
